import { getCrmHostname } from 'src/app/brandColorCache';

export const LS_API_URL = 'apiUrl';

// ── DEMO MODE (build-time, temporary) ───────────────────────────────────────
// A polished, fully-populated, all-Finnish demo build for the PJH client demo on
// renyy.wastehero.io (the `staging` branch on Vercel). Turned on ONLY by the
// REACT_APP_DEMO_MODE=true Vercel variable scoped to the `staging` Preview
// branch (see docs/deploy/vercel-env.md) — defaults OFF everywhere
// else, so development/master builds are completely unaffected. It is also
// hard-gated OFF on the production build (see isDemoMode below), so promoting
// the staging config to production can never surface the demo fixtures.
//
// When ON:
//  - every feature domain returns rich Finnish mock fixtures instead of hitting
//    the empty staging backend (see src/api/demoMock.ts + the per-domain guards);
//  - all `crm_enable_*` nav sections are forced on and the UI locale is pinned to
//    Finnish (see Company.store's applyDemoOverrides + the i18n seed);
//  - the act-on-behalf principals list is populated so the switcher renders.
// The address-search login stays REAL (real properties resolve), so only the
// post-login feature data is mock.
//
// Revert = flip the env flag off (or delete this block + its consumers). Because
// every demo behaviour is gated on isDemoMode(), nothing leaks into other builds.
//
// PER-TENANT OPT-OUT: demo is ON by default for every tenant on a DEMO_MODE
// build (PJH etc.), but certain tenants must hit the REAL backend for live
// testing (e.g. renyy-2.wastehero.io). Those are listed, comma-separated, in
// REACT_APP_DEMO_EXCLUDE_HOSTS — matching the REACT_APP_DEMO_MODE convention so
// the exclude list is adjustable from the Vercel env with no source change. The
// current tenant is resolved via getCrmHostname() (the app's single tenant
// identity source — query param / DEV_CRM_HOSTNAME / window.location.hostname),
// matched case-insensitively and trimmed against the list.

// Comma-separated hostnames that OPT OUT of demo mode even on a DEMO_MODE build.
// Trimmed + lower-cased; blanks dropped. Empty/unset ⇒ no tenant is excluded.
const parseDemoExcludeHosts = (): string[] =>
  (import.meta.env.REACT_APP_DEMO_EXCLUDE_HOSTS || '')
    .split(',')
    .map((host) => host.trim().toLowerCase())
    .filter(Boolean);

// True when the current tenant is in the exclude list (⇒ real backend, no demo).
const isDemoExcludedTenant = (): boolean => {
  if (typeof window === 'undefined') return false;
  const excluded = parseDemoExcludeHosts();
  if (excluded.length === 0) return false;
  return excluded.includes(getCrmHostname().trim().toLowerCase());
};

// Environment gate for DEMO DATA ONLY. Unlike isNonProductionEnv (the dev-pill
// predicate, which is deliberately fail-open on a missing label), demo fixtures
// must fail SAFE: a build has to positively declare itself non-production
// before Finnish mock data may reach a citizen. Allowed signals are the exact
// Vercel environment labels below, or a local dev build (`yarn start`), where
// REACT_APP_ENVIRONMENT is legitimately undefined. A production build, an empty
// label (env-pipeline regression) and any other label all get demo OFF — if a
// Vercel deploy that should run the demo carries a label outside this list, add
// that exact label here deliberately.
const DEMO_ALLOWED_ENVIRONMENTS = ['preview', 'development', 'staging'];

const isDemoAllowedEnv = (): boolean => {
  const environment = import.meta.env.REACT_APP_ENVIRONMENT;
  if (environment && DEMO_ALLOWED_ENVIRONMENTS.includes(environment))
    return true;
  return !environment && import.meta.env.MODE === 'development';
};

export const isDemoMode = (): boolean => {
  // Demo mode can never activate on a production build. Staging is the base
  // branch, and its Vercel variables turn demo mode on; without this guard a
  // staging-to-production promotion would carry the Finnish mock fixtures to
  // every tenant not on the exclude list.
  if (!isDemoAllowedEnv()) return false;
  return (
    import.meta.env.REACT_APP_DEMO_MODE === 'true' && !isDemoExcludedTenant()
  );
};

export const overrideApiUrl = (apiUrl: string) => {
  localStorage.setItem(LS_API_URL, apiUrl);
  window.location.reload();
};

export const getOverrideApiUrl = () => {
  return localStorage.getItem(LS_API_URL);
};

// Dev-only auth bypass. Two activation paths:
//  1. .env: REACT_APP_DEV_BYPASS_AUTH=true (development mode only)
//  2. localStorage: DEV_BYPASS_AUTH='1' (toggled via the dev pill, works in
//     preview/prod too once the pill is unlocked).
// Read at runtime — never cache at module load — so toggling the pill takes
// effect on the next render/reload without a rebuild.
export const LS_DEV_BYPASS_AUTH = 'DEV_BYPASS_AUTH';

export const isDevBypassAuth = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (
    import.meta.env.REACT_APP_DEV_BYPASS_AUTH === 'true' &&
    import.meta.env.MODE === 'development'
  ) {
    return true;
  }
  return localStorage.getItem(LS_DEV_BYPASS_AUTH) === '1';
};

export const setDevBypassAuth = (enabled: boolean) => {
  if (enabled) {
    localStorage.setItem(LS_DEV_BYPASS_AUTH, '1');
  } else {
    localStorage.removeItem(LS_DEV_BYPASS_AUTH);
  }
};

// Dev-only login-method override. The login surface the app renders is driven
// by the company config's `login_method`; this lets the dev pill force a value
// so you can preview each citizen login surface (email/password, Suomi.fi, …)
// without a tenant actually configured for it. Read once at company-store load
// and overlaid onto the company data — see Company.store's
// applyDevLoginMethodOverride. Null = use the tenant's real value.
export const LS_LOGIN_METHOD = 'DEV_LOGIN_METHOD';

// Mirrors the company `login_method` union (src/app/Company.store TValue),
// including the not-yet-built 'suomifi' (Suomi.fi / FTN) method.
export type DevLoginMethod =
  | 'none'
  | 'user'
  | 'bankid'
  | 'email_otp'
  | 'suomifi';

export const getLoginMethodOverride = (): DevLoginMethod | null => {
  if (typeof window === 'undefined') return null;
  const value = localStorage.getItem(LS_LOGIN_METHOD);
  return value ? (value as DevLoginMethod) : null;
};

export const setLoginMethodOverride = (method: DevLoginMethod | null) => {
  if (method) {
    localStorage.setItem(LS_LOGIN_METHOD, method);
  } else {
    localStorage.removeItem(LS_LOGIN_METHOD);
  }
};

// Dev-only section-flags override. The new portal nav sections (account,
// services, reports, settings/comms, messages, pools) are gated by
// per-tenant `crm_enable_*` company flags most tenants don't set yet — absence
// means hidden by convention, leaving the pages URL-only. This forces them all
// ON in this browser so the whole shipped surface is walkable for QA without
// backend changes. Read at company-store load and overlaid onto the company
// data — see Company.store's applyDevSectionFlagsOverride.
export const LS_SECTION_FLAGS = 'DEV_SECTION_FLAGS';

// True in every non-production environment: local `yarn start`
// (REACT_APP_ENVIRONMENT undefined) and Vercel preview deploys
// (REACT_APP_ENVIRONMENT === 'preview'). False only on the production build,
// where REACT_APP_ENVIRONMENT is wired to 'production' by build:vercel. Mirrors
// the `!== 'production'` guard the dev pill uses (DevBackendIndicator's
// isPreview) so a build correctly tagged production stays locked down. We match
// `!== 'production'` rather than `=== 'preview'` defensively: if that value is
// ever missing (env-pipeline regression), an unlabelled build is treated as
// non-production, never as production.
const isNonProductionEnv = (): boolean =>
  import.meta.env.REACT_APP_ENVIRONMENT !== 'production';

export const isDevSectionFlagsOverride = (): boolean => {
  if (typeof window === 'undefined') return false;
  // Gate the override to non-production. Without this, a production build still
  // honours DEV_SECTION_FLAGS='1' in localStorage and force-enables every gated
  // portal section — letting any user unlock the whole surface in prod.
  if (!isNonProductionEnv()) return false;
  return localStorage.getItem(LS_SECTION_FLAGS) === '1';
};

export const setDevSectionFlagsOverride = (enabled: boolean) => {
  if (enabled) {
    localStorage.setItem(LS_SECTION_FLAGS, '1');
  } else {
    localStorage.removeItem(LS_SECTION_FLAGS);
  }
};

// Citizen auth keys live in plain strings across User.store / Property.store /
// api/client. When the dev pill switches tenant or backend, the old citizen
// token is bound to the previous tenant — leaving it in place makes the new
// tenant's anonymous-login path skip, and tenant-scoped requests (property
// search, etc.) silently return empty against the wrong tenant.
export const clearCitizenAuth = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('AUTH_TOKEN');
  localStorage.removeItem('AUTH_TOKEN_EXPIRY');
  localStorage.removeItem('AUTH_PROPERTY_ID');
  localStorage.removeItem('AUTH_PROPERTY_NAME');
};

// Single read accessor for the citizen's selected property id. Centralizes the
// magic-string key shared by Property.store and CriptioLogin's MitID/NemID
// redirect builders. Returns null (not '') when unset so a falsy check is safe.
export const getAuthPropertyId = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('AUTH_PROPERTY_ID');
};
